import React from 'react';

import {
  Alert,
  AlertDescription,
  AlertTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@column/ui';

import { DateInput } from '~/components';

/**
 * Shared questionnaire field primitives for the dispute reason forms. All are controlled; the
 * per-reason form owns its state and passes values down. Sensitive answers are masked from
 * analytics/session replay with `data-dd-privacy="hidden"` on every input.
 */

export interface ReasonFieldProps {
  label: string;
  htmlFor?: string;
  required?: boolean;
  description?: string;
  error?: string;
  children: React.ReactNode;
}

export const ReasonField: React.FC<ReasonFieldProps> = ({ label, htmlFor, required, description, error, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </Label>
    {description && <p className="text-xs text-muted-foreground">{description}</p>}
    {children}
    {error && (
      <p className="text-xs text-destructive" data-testid="reason-field-error">
        {error}
      </p>
    )}
  </div>
);

export interface ReasonSelectFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  testId?: string;
}

export const ReasonSelectField: React.FC<ReasonSelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  description,
  required,
  error,
  disabled,
  testId,
}) => (
  <ReasonField label={label} required={required} error={error} description={description}>
    <Select value={value} disabled={disabled} onValueChange={onChange}>
      <SelectTrigger className="w-full" data-testid={testId} aria-label={label}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent position="popper" align="end" data-dd-privacy="hidden">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </ReasonField>
);

export interface ReasonTextFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  /** Most characters the backend accepts for the trimmed answer. Short limits show a counter. */
  maxLength?: number;
  multiline?: boolean;
  testId?: string;
}

// Limits at or below this size are short enough that a live counter helps; long descriptions only flag an overrun.
const COUNTER_MAX_LENGTH = 200;

export const ReasonTextField: React.FC<ReasonTextFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  description,
  required,
  error,
  disabled,
  maxLength,
  multiline,
  testId,
}) => {
  // Characters, not UTF-16 units or bytes, as the backend counts them.
  const characterCount = [...(value ?? '').trim()].length;
  const overLimit = maxLength !== undefined && characterCount > maxLength;

  return (
    <ReasonField label={label} required={required} error={error} description={description}>
      {multiline ? (
        <Textarea
          id={testId}
          value={value ?? ''}
          disabled={disabled}
          placeholder={placeholder}
          data-dd-privacy="hidden"
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Input
          id={testId}
          value={value ?? ''}
          disabled={disabled}
          placeholder={placeholder}
          hasError={overLimit}
          data-dd-privacy="hidden"
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {maxLength !== undefined && maxLength <= COUNTER_MAX_LENGTH && (
        <p className={`text-xs ${overLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
          {characterCount}/{maxLength} characters
        </p>
      )}
    </ReasonField>
  );
};

export interface ReasonDateFieldProps {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  testId?: string;
}

export const ReasonDateField: React.FC<ReasonDateFieldProps> = ({
  label,
  value,
  onChange,
  description,
  required,
  error,
  disabled,
  testId,
}) => (
  <ReasonField label={label} required={required} error={error} description={description}>
    <div data-testid={testId} data-dd-privacy="hidden">
      <DateInput value={value} onChange={onChange} isDisabled={disabled} hasError={Boolean(error)} />
    </div>
  </ReasonField>
);

/** The documents a dispute needs, from `evidenceGuidance`. Renders nothing without guidance. */
export const ReasonEvidenceGuidance: React.FC<{ guidance: string[] }> = ({ guidance }) =>
  guidance.length === 0 ? null : (
    <Alert data-testid="dispute-evidence-guidance">
      <AlertTitle>Documents for this dispute</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4">
          {guidance.map((entry) => (
            <li key={entry}>{entry}</li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );

/**
 * Yes/no question with an explicit unanswered state. `false` is a valid supplied answer and is
 * never conflated with unanswered; answers are never prefilled.
 */
export interface ReasonBooleanFieldProps {
  label: string;
  value: boolean | undefined;
  onChange: (value: boolean) => void;
  description?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  testId?: string;
}

export const ReasonBooleanField: React.FC<ReasonBooleanFieldProps> = ({
  label,
  value,
  onChange,
  description,
  required,
  error,
  disabled,
  testId,
}) => (
  <ReasonField label={label} description={description} required={required} error={error}>
    <div className="flex gap-4" data-testid={testId} data-dd-privacy="hidden" role="radiogroup" aria-label={label}>
      {[
        { label: 'Yes', checked: value === true },
        { label: 'No', checked: value === false },
      ].map((option) => (
        <label
          key={option.label}
          className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
          data-testid={`${testId}-option-${option.label.toLowerCase()}`}
        >
          <input
            type="radio"
            name={`${testId}-${option.label}`}
            checked={option.checked}
            disabled={disabled}
            onChange={() => onChange(option.label === 'Yes')}
            className="size-4 accent-primary"
          />
          {option.label}
        </label>
      ))}
    </div>
  </ReasonField>
);
